import React, { useState, useEffect } from 'react';
import { Users, Plus, Mail, Phone, Shield, Loader2, UserPlus } from 'lucide-react';
import { userAPI, authAPI } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import ImageUpload from '../components/ImageUpload';

const ROLES = [
  { id: 'company_admin', label: 'Company Admin', color: 'bg-red-500/20 text-red-500' },
  { id: 'manager', label: 'Manager', color: 'bg-orange-500/20 text-orange-500' },
  { id: 'technician', label: 'Technician', color: 'bg-blue-500/20 text-blue-500' },
];

export default function Team() {
  const { company } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'technician',
    password: '',
    avatar_url: null,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await userAPI.list();
      setUsers(data || []);
    } catch (error) {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      await authAPI.register({
        ...formData,
        company_id: company.id,
      });
      toast.success('Team member added successfully!');
      setDialogOpen(false);
      setFormData({ first_name: '', last_name: '', email: '', phone: '', role: 'technician', password: '', avatar_url: null });
      loadUsers();
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Failed to add team member';
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleConfig = (role) => ROLES.find((r) => r.id === role) || ROLES[2];

  return (
    <div className="space-y-6 fade-in" data-testid="team-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Team Management</h1>
          <p className="text-muted-foreground">Manage technicians and staff members</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="btn-gradient-primary" data-testid="add-member-button">
          <UserPlus className="w-4 h-4 mr-2" /> Add Team Member
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {ROLES.map((role) => {
          const count = users.filter((u) => u.role === role.id).length;
          return (
            <Card key={role.id} className="stat-blue border">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{role.label}s</p>
                <p className="text-2xl font-bold">{count}</p>
              </CardContent>
            </Card>
          );
        })}
        <Card className="stat-green border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Active</p>
            <p className="text-2xl font-bold text-green-500">{users.filter(u => u.is_active).length}</p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <Card><CardContent className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((u) => {
            const roleConfig = getRoleConfig(u.role);
            return (
              <Card key={u.id} className="card-hover" data-testid={`team-member-${u.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    {u.avatar_url ? (
                      <img
                        src={u.avatar_url}
                        alt={`${u.first_name} ${u.last_name}`}
                        className="w-14 h-14 rounded-full object-cover border-2 border-primary/30"
                        data-testid={`team-avatar-${u.id}`}
                      />
                    ) : (
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {u.first_name?.[0]}{u.last_name?.[0]}
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold">{u.first_name} {u.last_name}</h3>
                      <Badge className={`${roleConfig.color} border-0 mt-1`}>
                        <Shield className="w-3 h-3 mr-1" />
                        {roleConfig.label}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="truncate">{u.email}</span>
                    </div>
                    {u.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span>{u.phone}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 pt-3 border-t">
                    {u.is_active ? (
                      <Badge variant="outline" className="badge-active border-0">Active</Badge>
                    ) : (
                      <Badge variant="outline" className="badge-cancelled border-0">Inactive</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>Invite a new technician or staff member to your company</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Min 6 characters" required minLength={6} />
            </div>
            <div className="space-y-2">
              <Label>Profile Photo</Label>
              <ImageUpload
                value={formData.avatar_url}
                onChange={(v) => setFormData({ ...formData, avatar_url: v })}
                label="Upload tech photo"
              />
              <p className="text-xs text-muted-foreground">
                Customers see this photo in SMS notifications when this tech is assigned to their job.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-gradient-primary" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Member'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
